import type { VetCanStudioStepId } from "./studioReadModel";
import type { StudioScenarioPresetId } from "./scenarioPresets";

export const STUDIO_SCENARIO_PACKS = [
  {
    id: "clinic_demo",
    label: "Clinic demo",
    audience: "VetCan install-week walkthrough",
    summary:
      "Lead the founder-demo route with the clinic-safe front-desk walkthrough: Studio for the draft, Preview for rehearsal, and Dashboard for live operational truth.",
    showcaseFacts: [
      "Reference profile: current install packet and approved clinic branding",
      "Support line: owner-confirmed during install wiring",
      "Preview scenario: Receptionist greeting",
      "Scenario focus: Renewal clarification / veteran-funding boundary",
    ],
    entryStep: "preview",
    entryStepLabel: "Preview",
    previewScenarioId: "receptionist_greeting",
    supportedScenarioIds: [
      "receptionist_greeting",
      "scheduling_assist",
      "human_handoff",
      "renewal_clarification",
      "veteran_funding_boundary",
    ],
    verticalCategory: "clinic",
    whiteLabelSafe: true,
    routeBoundary: "internal_preview_only",
    boundaryNote:
      "White-label content stays packaged as showcase material only. Public tenant routing remains separate.",
  },
  {
    id: "general_service_business",
    label: "General service-business demo",
    audience: "Founder pitch / broad service showcase",
    summary:
      "Show a premium concierge flow for high-trust service businesses that need scheduling clarity, objections handled cleanly, and visible rollout guardrails.",
    showcaseFacts: [
      "Best first step: Describe assistant",
      "Preview scenario: Objection handling",
      "Scenario focus: Payment not ready / distance-timing hesitation / renewal clarification",
      "Export focus: Premium handoff package",
    ],
    entryStep: "describe_assistant",
    entryStepLabel: "Describe assistant",
    previewScenarioId: "objection_handling",
    supportedScenarioIds: [
      "receptionist_greeting",
      "objection_handling",
      "payment_not_ready",
      "distance_timing_hesitation",
      "renewal_clarification",
      "veteran_funding_boundary",
      "human_handoff",
    ],
    verticalCategory: "general_service_business",
    whiteLabelSafe: true,
    routeBoundary: "internal_preview_only",
    boundaryNote:
      "Stays inside the Studio draft layer and never touches live clinic routing.",
  },
  {
    id: "creator_demo",
    label: "Creator demo",
    audience: "Creator concierge / sponsorship screening",
    summary:
      "Package the Studio flow for creator-style inbound triage, polished handoffs, and premium voice polish without widening medical or booking scope.",
    showcaseFacts: [
      "Best first step: Persona",
      "Preview scenario: Human handoff",
      "Voice polish stays preview-only",
    ],
    entryStep: "persona",
    entryStepLabel: "Persona",
    previewScenarioId: "human_handoff",
    supportedScenarioIds: [
      "human_handoff",
      "objection_handling",
      "premium_voice_polish_preview",
    ],
    verticalCategory: "creator",
    whiteLabelSafe: true,
    routeBoundary: "internal_preview_only",
    boundaryNote:
      "Keeps recommendations inside supported tone, handoff, and preview capabilities.",
  },
  {
    id: "franchise_multi_site",
    label: "Franchise / multi-site demo",
    audience: "Multi-location rollout discussion",
    summary:
      "Show how Studio can rehearse routing clarity, callback capture, and scaled handoff posture for multi-site operators while the live switchboard stays blocked.",
    showcaseFacts: [
      "Best first step: Transfer rules",
      "Preview scenario: Scheduling assist",
      "Scenario focus: Transfer intake clarification / distance-location hesitation / callback rescue / transfer-payment fallback",
      "Export focus: Deploy-readiness blocker story",
    ],
    entryStep: "transfer_rules",
    entryStepLabel: "Transfer rules",
    previewScenarioId: "scheduling_assist",
    supportedScenarioIds: [
      "scheduling_assist",
      "human_handoff",
      "objection_handling",
      "transfer_intake_clarification",
      "distance_location_renewal_transfer",
      "callback_rescue",
      "transfer_payment_fallback",
    ],
    verticalCategory: "franchise_multi_site",
    whiteLabelSafe: true,
    routeBoundary: "internal_preview_only",
    boundaryNote:
      "Launch-control truth and rollout blockers remain visible for every showcase variant.",
  },
] as const;

export type StudioScenarioPack = (typeof STUDIO_SCENARIO_PACKS)[number];
export type StudioScenarioPackId = StudioScenarioPack["id"];
export type StudioScenarioVerticalCategory = StudioScenarioPack["verticalCategory"];

export function isStudioScenarioPackId(
  value: string | null | undefined
): value is StudioScenarioPackId {
  return STUDIO_SCENARIO_PACKS.some((pack) => pack.id === value);
}

export function getStudioScenarioPack(
  packId: StudioScenarioPackId
): StudioScenarioPack {
  return STUDIO_SCENARIO_PACKS.find((pack) => pack.id === packId) || STUDIO_SCENARIO_PACKS[0];
}

export function getStudioScenarioPacksForScenario(
  scenarioId: StudioScenarioPresetId
): StudioScenarioPack[] {
  return STUDIO_SCENARIO_PACKS.filter((pack) =>
    pack.supportedScenarioIds.includes(scenarioId)
  );
}

export function getStudioScenarioPackEntryStep(
  packId: StudioScenarioPackId
): VetCanStudioStepId {
  return getStudioScenarioPack(packId).entryStep;
}
